import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowLeft, FaUsers } from "react-icons/fa";
import { BsPinMapFill } from "react-icons/bs";
import { MdNotificationsActive, MdClass } from "react-icons/md";
import { FaTemperatureLow } from "react-icons/fa6";

import EAIMLogo from "../img/TMIS_logo.png";
import "./Room.css";
import FloorPlan from "./test";

import TempPng from "../img/temperature.png";
import HumidityPng from "../img/humidity.png";

const EAIM_PINK = "#b022a2";
const EAIM_GREEN = "#066f3c";
const EAIM_BLUE = "#2f78c8";
const EAIM_ORANGE = "#ff8a00";

const DATA_REFRESH_SEC = 30;

const ROOM_COLOR_RULES = [
  { prefix: "A", min: 8, max: 13, color: EAIM_ORANGE },
  { prefix: "A", min: 4, max: 7, color: EAIM_GREEN },
  { prefix: "A", min: 1, max: 3, color: EAIM_PINK },
  { prefix: "B", min: 3, max: 11, color: EAIM_GREEN },
  { prefix: "B", min: 2, max: 2, color: EAIM_BLUE },
  { prefix: "C", min: 1, max: 6, color: EAIM_BLUE },
  { prefix: "C", min: 7, max: 8, color: EAIM_PINK },
];

function getRoomColor(code) {
  const s = String(code || "").trim().toUpperCase();
  const m = s.match(/^([A-Z])\s*0*([0-9]+)$/);
  if (!m) return "#888";
  const prefix = m[1];
  const num = Number(m[2]);
  const rule = ROOM_COLOR_RULES.find(
    (r) => r.prefix === prefix && num >= r.min && num <= r.max
  );
  return rule?.color || "#888";
}

const pad2 = (n) => String(n).padStart(2, "0");
const ymd = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function safeNum(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function pick(obj, keys, fallback = "") {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
}

function timeToSeconds(t) {
  const s = String(t || "").trim();
  if (!s) return null;
  const parts = s.split(":").map((x) => parseInt(x, 10));
  if (parts.some((n) => Number.isNaN(n))) return null;
  const [hh = 0, mm = 0, ss = 0] = parts;
  return hh * 3600 + mm * 60 + ss;
}

function overlapSessionWithAttendance(session, attRow) {
  const sessRoom = pick(session, ["room", "roomName", "room_name"], "");
  const attRoom = pick(attRow, ["room_name", "room", "roomName"], "");
  if (sessRoom && attRoom && String(attRoom) !== String(sessRoom)) return false;

  const eDate = pick(attRow, ["event_date", "date"], "");
  const eTime = pick(attRow, ["event_time", "time"], "");
  if (!eDate || !eTime) return true;

  const e = new Date(`${eDate}T${eTime}`);
  if (Number.isNaN(e.getTime())) {
    const sec = timeToSeconds(eTime);
    if (sec == null) return true;

    const startSec =
      session?.start instanceof Date
        ? session.start.getHours() * 3600 +
          session.start.getMinutes() * 60 +
          session.start.getSeconds()
        : null;

    const endSec =
      session?.end instanceof Date
        ? session.end.getHours() * 3600 +
          session.end.getMinutes() * 60 +
          session.end.getSeconds()
        : null;

    if (startSec == null || endSec == null) return true;
    return sec >= startSec && sec <= endSec;
  }

  return e >= session.start && e <= session.end;
}

function formatHHmm(d = new Date()) {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export default function ActiveSessionRooms() {
  const [auto, setAuto] = useState(true);

  const [tempC, setTempC] = useState(24.5);
  const [humidity, setHumidity] = useState(60);

  const [envUpdatedAt, setEnvUpdatedAt] = useState("");

  const [tabSwitchSec, setTabSwitchSec] = useState(30);
  const [activeTab, setActiveTab] = useState("ACTIVE ROOMS");

  const [totalRooms] = useState(32);
  const [activeRoomsCount, setActiveRoomsCount] = useState(0);
  const [totalPaxNow, setTotalPaxNow] = useState(0);

  const [currentClasses, setCurrentClasses] = useState([]);

  const API_BASE = (process.env.REACT_APP_API || "").replace(/\/+$/, "");

  const refreshingRef = useRef(false);

  const computeNowStats = useMemo(() => {
    return async () => {
      const now = new Date();
      const today = ymd(now);

      const startOfToday = new Date(now);
      startOfToday.setHours(0, 0, 0, 0);

      const endOfToday = new Date(startOfToday);
      endOfToday.setDate(endOfToday.getDate() + 1);

      const qs = new URLSearchParams({
        from: startOfToday.toISOString(),
        to: endOfToday.toISOString(),
      });

      const schedRes = await fetch(`${API_BASE}/roomSchedules?${qs.toString()}`, {
        headers: { Accept: "application/json" },
      });
      const schedJson = await schedRes.json();

      const schedules = (schedJson?.data || []).map((x) => ({
        ...x,
        start: new Date(x.start),
        end: new Date(x.end),
      }));

      const attRes = await fetch(`${API_BASE}/getDailyAttendance?date=${today}`, {
        headers: { Accept: "application/json" },
      });
      const attJson = await attRes.json();
      const attendance = attJson?.data || [];

      const currentSessions = schedules.filter((s) => now >= s.start && now <= s.end);

      const activeRooms = new Set(
        currentSessions.map((s) => pick(s, ["room", "roomName", "room_name"], ""))
      );
      activeRooms.delete("");
      setActiveRoomsCount(activeRooms.size);

      let totalPax = 0;
      const rows = [];

      for (const s of currentSessions) {
        const roomName = pick(s, ["room", "roomName", "room_name"], "");

        const classCode = pick(
          s,
          ["code", "room_code", "roomCode", "class_code", "classCode", "intake"],
          roomName || "CLASS"
        );

        const subjectFromSchedule = pick(
          s,
          ["subject", "subject_name", "course", "course_name", "module", "title"],
          ""
        );

        const matched = attendance.filter((e) => overlapSessionWithAttendance(s, e));

        const present = matched.reduce(
          (sum, e) => sum + safeNum(pick(e, ["totalPresent", "present"], 0)),
          0
        );

        const absent = matched.reduce(
          (sum, e) => sum + safeNum(pick(e, ["totalAbsent", "absent"], 0)),
          0
        );

        const totalFromBackend = matched.reduce((sum, e) => {
          const t = pick(e, ["ctrStudent", "ctr_student", "student_count"], null);
          return sum + safeNum(t);
        }, 0);

        const total = Math.max(present + absent, totalFromBackend, present);

        const subjectFromBackend = matched[0]
          ? String(pick(matched[0], ["subject_code", "subject"], ""))
          : "";
        const subject = subjectFromSchedule || subjectFromBackend || "—";

        totalPax += present;

        rows.push({
          code: String(classCode).toUpperCase(),
          subject,
          total,
          present,
          absent,
          roomName,
          classNo: matched[0] ? pick(matched[0], ["class_no"], "") : "",
        });
      }

      rows.sort((a, b) => b.present - a.present);

      setTotalPaxNow(totalPax);
      setCurrentClasses(rows);
    };
  }, [API_BASE]);

  async function fetchAveEnv() {
    const r = await fetch(`${API_BASE}/getCurrAveTempAndHumid`, {
      headers: { Accept: "application/json" },
    });
    const json = await r.json();

    if (!r.ok || !json?.success) {
      console.warn("getCurrAveTempAndHumid failed:", json);
      return false;
    }

    const row = Array.isArray(json.data) ? json.data[0] : null;
    if (!row) return false;

    const aveTemp = Number(row.average_temp);
    const aveHumid = Number(row.average_humidity);

    if (Number.isFinite(aveTemp)) setTempC(aveTemp);
    if (Number.isFinite(aveHumid)) setHumidity(aveHumid);

    return true;
  }

  useEffect(() => {
    let id;

    const run = async () => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;

      try {
        const [statsOk, envOk] = await Promise.allSettled([
          computeNowStats(),
          fetchAveEnv(),
        ]);

        const envSuccess =
          envOk.status === "fulfilled" && envOk.value === true;
        if (envSuccess) {
          setEnvUpdatedAt(formatHHmm(new Date()));
        }
      } catch (e) {
        console.error("refresh run error:", e);
      } finally {
        refreshingRef.current = false;
      }
    };

    run(); 
    id = setInterval(run, DATA_REFRESH_SEC * 1000);

    return () => clearInterval(id);
  }, [computeNowStats, API_BASE]);


  useEffect(() => {
    if (!auto) return;

    const ms = Math.max(5, Number(tabSwitchSec || 5)) * 1000;

    const id = setInterval(() => {
      setActiveTab((prev) =>
        prev === "ACTIVE ROOMS" ? "ENVIRONMENT" : "ACTIVE ROOMS"
      );
    }, ms);

    return () => clearInterval(id);
  }, [auto, tabSwitchSec]);

  return (
    <div className="asr-page">
      <aside className="asr-sidebar">
        <img src={EAIMLogo} alt="EAIM Logo" className="asr-logo" style={{width: "70%", marginLeft: "40px"}} />

        <div className="asr-sidebarTitle">Digital Campus Map</div>

        <div className="asr-metricList">
          <Metric icon={<MdClass size={30} />} value={String(totalRooms)} label="Total Classrooms" />
          <Metric icon={<MdNotificationsActive size={30} />} value={String(activeRoomsCount)} label="Active Classrooms" />
          <Metric icon={<FaUsers size={30} />} value={String(totalPaxNow)} label="Total Pax In Class" />
        </div>

        <div className="weatherStack">
          <WeatherCard
            titleRight="Temperature (Avg)"
            bigValue={Number.isFinite(tempC) ? `${tempC.toFixed(1)}°C` : "—"}
            subLeft={envUpdatedAt ? `Updated at: ${envUpdatedAt}` : "Updating..."}
            location="Campus-wide"
            iconType="temp"
          />
          <WeatherCard
            titleRight="Relative Humidity"
            bigValue={Number.isFinite(humidity) ? `${humidity.toFixed(0)}%` : "—"}
            subLeft={envUpdatedAt ? `Updated at: ${envUpdatedAt}` : "Updating..."}
            location="Campus-wide"
            iconType="humidity"
          />
        </div>
      </aside>

      <main className="asr-main">
        <header className="asr-topbar">
          <div className="asr-leftCluster">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="asr-backBtn"
              aria-label="Back"
              title="Back"
            >
              <FaArrowLeft />
            </button>
            <div className="asr-headerTitle">Active Session Rooms</div>
          </div>

          <div className="asr-tabsCenter">
            <IconTab
              icon={<BsPinMapFill />}
              label="ACTIVE ROOMS"
              active={activeTab === "ACTIVE ROOMS"}
              onClick={() => setActiveTab("ACTIVE ROOMS")}
            />
            <IconTab
              icon={<FaTemperatureLow />}
              label="ENVIRONMENT"
              active={activeTab === "ENVIRONMENT"}
              onClick={() => setActiveTab("ENVIRONMENT")}
            />
          </div>

          <div className="asr-rightCluster">
            <div className="asr-intervalWrap">
              <select
                value={tabSwitchSec}
                onChange={(e) => setTabSwitchSec(Number(e.target.value))}
                className="asr-select"
                title="Auto tab switch interval"
              >
                <option value={10}>10 sec</option>
                <option value={20}>20 sec</option>
                <option value={30}>30 sec</option>
                <option value={45}>45 sec</option>
                <option value={60}>60 sec</option>
              </select>
            </div>

            <div className="asr-toggleWrap">
              <div
                role="switch"
                aria-checked={auto}
                tabIndex={0}
                onClick={() => setAuto((v) => !v)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && setAuto((v) => !v)
                }
                className={`asr-toggle ${auto ? "is-on" : ""}`}
                title="Auto"
              >
                <div className="asr-knob" />
              </div>
              <div className="asr-autoText">AUTO</div>
            </div>
          </div>
        </header>

        <section className="asr-contentRow">
          <div className="asr-tablePane">
            <CurrentClassTable rows={currentClasses} />
          </div>

          <div className="asr-mapPane">
            <FloorPlan view={activeTab} />
          </div>
        </section>
      </main>
    </div>
  );
}

function Metric({ icon, value, label }) {
  return (
    <div className="asr-metricRow">
      <div className="asr-metricIcon">{icon}</div>
      <div className="asr-metricValue">{value}</div>
      <div className="asr-metricLabel">{label}</div>
    </div>
  );
}

function IconTab({ icon, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`asr-iconTab ${active ? "is-active" : ""}`}
    >
      <div className={`asr-iconTabIcon ${active ? "is-active" : ""}`}>{icon}</div>
      <div className={`asr-iconTabLabel ${active ? "is-active" : ""}`}>{label}</div>
    </button>
  );
}

function WeatherCard({ titleRight, bigValue, subLeft, location, iconType }) {
  const iconSrc = iconType === "temp" ? TempPng : HumidityPng;

  return (
    <div className="weatherCard">
      <WeatherBg />

      <div className="weatherIcon" aria-hidden="true">
        <img
          src={iconSrc}
          alt={iconType === "temp" ? "Temperature" : "Humidity"}
          className="weatherIconImg"
          draggable="false"
        />
      </div>

      <p className="weatherMainText">{bigValue}</p>

      <div className="weatherInfo">
        <div className="weatherInfoLeft">
          <p className="weatherTextGray">{subLeft}</p>
          <p>{location}</p>
        </div>
        <p className="weatherInfoRight">{titleRight}</p>
      </div>
    </div>
  );
}

function WeatherBg() {
  return (
    <svg
      fill="none"
      viewBox="0 0 342 175"
      xmlns="http://www.w3.org/2000/svg"
      className="weatherBg"
      preserveAspectRatio="none"
    >
      <rect
        width="342"
        height="175"
        rx="18"
        fill="url(#weatherGradient)"
      />

      <defs>
        <linearGradient
          id="weatherGradient"
          x1="0"
          y1="0"
          x2="342"
          y2="175"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#2E4EA8" />
          <stop offset="55%" stopColor="#203A8F" />
          <stop offset="100%" stopColor="#172D75" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function CurrentClassTable({ rows }) {
  return (
    <div className="cc-card">
      <div className="cc-headerRow">
        <div className="cc-head cc-codeCol">ROOMS</div>
        <div className="cc-head cc-subjectCol">SUBJECTS</div>
        <div className="cc-head cc-numCol">CLASS</div>
        <div className="cc-head cc-numCol">P</div>
        <div className="cc-head cc-numCol">A</div>
      </div>

      <div className="cc-body">
        {rows?.length ? (
          rows.slice(0, 12).map((r, idx) => (
            <div key={`${r.code}-${idx}`} className="cc-row">
              <div
                className="cc-codePill"
                style={{ background: getRoomColor(r.code) }}
                title={r.code}
              >
                {r.code}
              </div>

              <div className="cc-subject">{r.subject}</div>

              <div className="cc-total">{safeNum(r.total)}</div>
              <div className="cc-present">{safeNum(r.present)}</div>
              <div className="cc-absent">{safeNum(r.absent)}</div>
            </div>
          ))
        ) : (
          <div className="cc-empty">No active classes right now.</div>
        )}
      </div>
    </div>
  );
}
